import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useIsMutating } from "@tanstack/react-query";
import { Check, ChevronDown } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import {
  CHAIN_OPTIONS,
  useNetworkStore,
} from "@/stores/useNetworkStore";
import { getSupportedChain, isMainnetChainId } from "@/config/activeChain";
import { useUIStore } from "@/stores/useUIStore";
import { cn } from "@/lib/utils";

/**
 * Testnet / Mainnet switch for the chain the whole app runs against.
 *
 * Callers own the admin gate; this component never checks the role itself.
 * Choosing a mainnet chain asks first -- past that point every transaction
 * moves real funds -- and either choice reloads the page (see useNetworkStore).
 *
 * The switch is disabled while any mutation is in flight. Chain writes here are
 * two-phase (on-chain first, Supabase second) with no automated reconciliation,
 * so reloading between the two phases strands funds that already moved.
 *
 * `compact` is the top-bar variant (dropdown, fits next to the other chrome
 * controls); the default is the wider segmented control.
 */
function NetworkSwitcher({
  compact = false,
  className,
}: {
  compact?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const chainId = useNetworkStore((s) => s.chainId);
  const switchChain = useNetworkStore((s) => s.switchChain);
  const changedElsewhere = useNetworkStore((s) => s.changedElsewhere);
  const reloadOntoStoredChain = useNetworkStore((s) => s.reloadOntoStoredChain);
  const addToast = useUIStore((s) => s.addToast);
  const [pendingMainnet, setPendingMainnet] = useState<number | null>(null);

  // Any in-flight mutation may be mid-transaction: the on-chain write lands
  // before the Supabase write, and a reload between them leaves funds moved
  // with nothing recorded.
  const busy = useIsMutating() > 0;

  const active = getSupportedChain(chainId) ?? CHAIN_OPTIONS[0];
  const groupLabel = t("network.label", "Network");

  function apply(nextChainId: number) {
    if (switchChain(nextChainId)) return;
    addToast({
      type: "error",
      message: t(
        "network.switchFailed",
        "Could not save the network choice, so nothing changed. Check that this browser allows site storage.",
      ),
    });
  }

  // Moving to a mainnet chain is the one direction worth a speed bump: it
  // repoints the wallet and every contract call at real money.
  function select(nextChainId: number) {
    if (nextChainId === chainId || busy) return;
    if (isMainnetChainId(nextChainId)) {
      setPendingMainnet(nextChainId);
      return;
    }
    apply(nextChainId);
  }

  const busyTitle = busy
    ? t("network.busy", "Finish the transaction in progress before switching networks")
    : undefined;

  const confirmModal = (
    <Modal
      open={pendingMainnet !== null}
      onOpenChange={(open) => !open && setPendingMainnet(null)}
      title={t("network.confirmTitle", "Switch to Mainnet (Polygon)?")}
      description={t(
        "network.confirmBody",
        "The whole app moves to Polygon mainnet: quests, balances and every transaction will use real funds. The page reloads to apply it.",
      )}
    >
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={() => setPendingMainnet(null)}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          variant="danger"
          onClick={() => {
            const target = pendingMainnet;
            setPendingMainnet(null);
            if (target !== null) apply(target);
          }}
        >
          {t("network.confirmAction", "Switch to Mainnet")}
        </Button>
      </div>
    </Modal>
  );

  // Another tab moved the stored chain. This tab is still on its original one,
  // so offer a deliberate reload rather than letting it flip silently later.
  const elsewhereNotice = changedElsewhere !== null && (
    <button
      type="button"
      onClick={reloadOntoStoredChain}
      className="rounded-chip border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-400"
    >
      {t("network.changedElsewhere", "Network changed in another tab — reload")}
    </button>
  );

  if (!compact) {
    return (
      <>
        <div className="flex shrink-0 items-center gap-2">
          {elsewhereNotice}
          <div
            role="group"
            aria-label={groupLabel}
            title={busyTitle}
            className={cn(
              "flex shrink-0 items-center gap-1 rounded-lg border border-border-default bg-surface-secondary p-1",
              busy && "opacity-50",
              className,
            )}
          >
            {CHAIN_OPTIONS.map((option) => {
              const isActive = chainId === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => select(option.id)}
                  disabled={busy}
                  aria-pressed={isActive}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed",
                    isActive
                      ? "bg-surface text-text-primary shadow-sm"
                      : "text-text-muted hover:text-text-secondary",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "mr-1.5 inline-block h-2 w-2 rounded-full",
                      option.dotClass,
                      !isActive && "opacity-40",
                    )}
                  />
                  {t(option.labelKey, option.fallback)}
                </button>
              );
            })}
          </div>
        </div>
        {confirmModal}
      </>
    );
  }

  return (
    <>
      {elsewhereNotice}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild disabled={busy}>
          <button
            type="button"
            disabled={busy}
            title={busyTitle}
            aria-label={`${groupLabel}: ${t(active.labelKey, active.fallback)}`}
            className={cn(
              "flex items-center gap-1.5 rounded-chip border px-2 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50",
              isMainnetChainId(chainId)
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : "border-border-default bg-surface-secondary text-text-secondary hover:text-text-primary",
              className,
            )}
          >
            <span
              aria-hidden="true"
              className={cn("inline-block h-2 w-2 rounded-full", active.dotClass)}
            />
            <span className="hidden sm:inline">
              {t(active.shortKey, active.shortFallback)}
            </span>
            <ChevronDown className="h-3 w-3 opacity-70" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="z-50 min-w-[180px] rounded-xl border border-border-default bg-surface p-1 shadow-lg animate-in fade-in-0 zoom-in-95"
            sideOffset={8}
            align="end"
          >
            <DropdownMenu.Label className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
              {groupLabel}
            </DropdownMenu.Label>
            {CHAIN_OPTIONS.map((option) => {
              const isActive = chainId === option.id;
              return (
                <DropdownMenu.Item
                  key={option.id}
                  className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-text-secondary outline-none hover:bg-surface-tertiary focus:bg-surface-tertiary"
                  onSelect={() => select(option.id)}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "inline-block h-2 w-2 shrink-0 rounded-full",
                      option.dotClass,
                    )}
                  />
                  <span className="flex-1">
                    {t(option.labelKey, option.fallback)}
                  </span>
                  {isActive && (
                    <Check className="h-3.5 w-3.5 shrink-0 text-success-500" />
                  )}
                </DropdownMenu.Item>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {confirmModal}
    </>
  );
}

export { NetworkSwitcher };
